import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { prefersReducedMotion } from '../../lib/gsap-setup.js';

// Variant timings are spec literals (epics.md line 3228, UX-spec line 183):
// - container.exit: 120ms opacity fade-out
// - container.visible.staggerChildren: 40ms between children
// - item.visible: 12px translateY → 0 over 120ms
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
    },
  },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.12 } },
};

// Exported for Vitest introspection — tests assert the 120ms exit / 40ms
// stagger / 12px rise literals against the same constants the runtime uses.
export const pageTransitionContainerVariants = containerVariants;
export const pageTransitionItemVariants = itemVariants;

interface PageTransitionProps {
  children: ReactNode;
}

export function PageTransition({ children }: PageTransitionProps) {
  // Root-level MotionConfig reducedMotion="user" zeros transforms but still
  // schedules staggerChildren delays, so a 10-section landing page would pause
  // ~400ms before painting. Short-circuit to plain <div> for instant paint.
  if (prefersReducedMotion) return <div>{children}</div>;
  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" exit="exit">
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children }: { children: ReactNode }) {
  if (prefersReducedMotion) return <div>{children}</div>;
  return <motion.div variants={itemVariants}>{children}</motion.div>;
}
